import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CloudUpload } from '@mui/icons-material';
import './Import.css';
import { useUserStorage } from '../services/userDataStorage';
import { GridData } from '../services/firestoreService';
import { useUser } from '@clerk/clerk-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import ConfirmDialog from './ConfirmDialog';

interface ImportProps {
  onSidebarRefresh?: () => void;
}

const Import: React.FC<ImportProps> = ({ onSidebarRefresh }) => {
  const navigate = useNavigate();
  const userStorage = useUserStorage();
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Constants for import limits
  const MAX_IMPORT_ROWS = 5000;
  const CHUNK_SIZE = 1000;
  const ALLOWED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];
  const MAX_FILE_SIZE = '10MB';

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!userStorage) {
      toast.error('Storage not available. Please try again.');
      return;
    }

    // Validate file type
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error(`Unsupported file type. Please use: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size too large. Maximum size is 10MB.');
      return;
    }

    setIsProcessing(true);
    setProcessingMessage('Reading file...');

    try {
      const data = await parseFile(file);
      
      // Check if file is too large
      if (data.length > MAX_IMPORT_ROWS) {
        setConfirmDialog({
          isOpen: true,
          title: 'Large File Import',
          message: `This file contains ${data.length.toLocaleString()} rows. For performance reasons, only the first ${MAX_IMPORT_ROWS.toLocaleString()} rows will be imported. Do you want to continue?`,
          onConfirm: () => {
            confirmFileImportWithData(file, data);
          }
        });
        setIsProcessing(false);
        return;
      }
      
      await processFileImport(file, data);
      
    } catch (error) {
      console.error('Failed to import file:', error);
      toast.error(`Failed to import file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsProcessing(false);
    }
  };

  const confirmFileImportWithData = async (file: File, data: string[][]) => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    setIsProcessing(true);
    
    try {
      await processFileImport(file, data);
    } catch (error) {
      console.error('Failed to import file:', error);
      toast.error(`Failed to import file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsProcessing(false);
    }
  };

  const cancelFileImport = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
    setIsProcessing(false);
  };

  const processFileImport = async (file: File, data: string[][]) => {
    setProcessingMessage('Creating spreadsheet...');
    
    try {
      const newGrid = await createGridFromData(data, file.name);
      
      // Trigger sidebar refresh
      if (onSidebarRefresh) {
        onSidebarRefresh();
      }
      
      toast.success(`File "${file.name}" imported successfully!`);
      
      // Navigate to the new grid
      navigate(`/sheets/${newGrid.id}`);
      
    } finally {
      setIsProcessing(false);
    }
  };

  // Parse file based on extension
  const parseFile = async (file: File): Promise<string[][]> => {
    const extension = file.name.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'csv':
        return parseCSV(file);
      case 'xlsx':
      case 'xls':
        return parseExcel(file);
      default:
        throw new Error(`Unsupported file type: ${extension}`);
    }
  };

  // Parse CSV file using Papa Parse
  const parseCSV = (file: File): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
      const results: string[][] = [];
      let rowCount = 0;
      
      Papa.parse(file, {
        step: (row) => {
          if (row.data && Array.isArray(row.data) && row.data.some((cell: any) => cell && String(cell).trim() !== '')) {
            results.push(row.data as string[]);
            rowCount++;
            
            if (rowCount >= MAX_IMPORT_ROWS + 1) {
              return;
            }
          }
        },
        complete: () => {
          if (results.length === 0) {
            reject(new Error('The CSV file appears to be empty or contains no valid data.'));
            return;
          }
          resolve(results);
        },
        error: (error) => {
          reject(new Error(`Failed to parse CSV: ${error.message}`));
        },
        header: false,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        transform: (value) => String(value).trim()
      });
    });
  };

  // Parse Excel file using xlsx
  const parseExcel = (file: File): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            reject(new Error('No worksheets found in the Excel file.'));
            return;
          }
          
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1, 
            defval: '', 
            blankrows: false 
          }) as string[][];
          
          const filteredData = jsonData.filter(row => 
            row && row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')
          );
          
          if (filteredData.length === 0) {
            reject(new Error('The Excel file appears to be empty or contains no valid data.'));
            return;
          }
          
          const stringData = filteredData.map(row => 
            row.map(cell => String(cell || '').trim())
          );
          
          resolve(stringData);
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read the file.'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  // Create grid from parsed data
  const createGridFromData = async (data: string[][], fileName: string): Promise<GridData> => {
    if (!userStorage) {
      throw new Error('User storage not available');
    }

    const baseName = fileName.replace(/\.[^/.]+$/, '');
    const gridTitle = baseName || 'Imported Sheet';

    const hasData = data.length > 0;
    if (!hasData) {
      throw new Error('No data found in the file');
    }

    const limitedData = data.slice(0, MAX_IMPORT_ROWS + 1);
    const firstRow = limitedData[0];
    const hasHeaders = firstRow && firstRow.length > 0 && 
      firstRow.every(cell => cell && cell.trim() !== '' && isNaN(Number(cell)));

    let headers: string[];
    let dataRows: string[][];

    if (hasHeaders) {
      headers = firstRow;
      dataRows = limitedData.slice(1);
    } else {
      const maxColumns = Math.max(...limitedData.map(row => row.length));
      headers = Array.from({ length: Math.min(maxColumns, 50) }, (_, i) => 
        `Column ${String.fromCharCode(65 + i)}`
      );
      dataRows = limitedData;
    }

    const MAX_COLUMNS = 50;
    if (headers.length > MAX_COLUMNS) {
      headers = headers.slice(0, MAX_COLUMNS);
      dataRows = dataRows.map(row => row.slice(0, MAX_COLUMNS));
    }

    if (headers.length === 0) {
      headers = ['Column A'];
    }

    const columnConfigs = headers.map((header, index) => ({
      id: index,
      name: header.trim(),
      options: {},
      width: 200,
    }));

    const minColumns = Math.max(10, headers.length);
    if (columnConfigs.length < minColumns && minColumns <= MAX_COLUMNS) {
      for (let i = columnConfigs.length; i < minColumns; i++) {
        columnConfigs.push({
          id: i,
          name: '',
          options: {},
          width: 200,
        });
      }
    }

    const gridData: any[] = [];
    
    for (let i = 0; i < dataRows.length; i += CHUNK_SIZE) {
      const chunk = dataRows.slice(i, i + CHUNK_SIZE);
      
      const chunkRows = chunk.map((row, chunkIndex) => {
        const rowIndex = i + chunkIndex;
        const gridRow: any = { id: rowIndex + 1 };
        
        columnConfigs.forEach((config, colIndex) => {
          gridRow[config.id] = row[colIndex] || '';
        });
        
        return gridRow;
      });
      
      gridData.push(...chunkRows);
      
      if (i + CHUNK_SIZE < dataRows.length) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }

    const minRows = Math.max(50, gridData.length);
    if (gridData.length < 50) {
      while (gridData.length < minRows) {
        const emptyRow: any = { id: gridData.length + 1 };
        columnConfigs.forEach(config => {
          emptyRow[config.id] = '';
        });
        gridData.push(emptyRow);
      }
    }

    const newGrid: GridData = {
      id: generateGridId(),
      title: gridTitle,
      gridData,
      columnConfigs,
      created: Date.now(),
      lastModified: Date.now(),
      userId: user?.id || ''
    };

    await userStorage.saveGrid(newGrid);
    return newGrid;
  };

  const generateGridId = (): string => {
    return `grid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  if (isProcessing) {
    return (
      <div className="import-container">
        <div className="import-header">
          <h1>Import</h1>
        </div>
        <div className="loading-container">
          <div className="spinner" />
          <p>{processingMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="import-container">
      <div className="import-header">
        <h1>Import</h1>
      </div>
      
      <div className="import-content">
        <div 
          className={`upload-zone ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClickUpload}
        >
          <CloudUpload className="upload-icon" />
          <h3>Drop file or select to upload</h3>
          <p>Choose a file to import into a new spreadsheet</p>
        </div>
        
        <div className="upload-info">
          <p>Supported formats: {ALLOWED_EXTENSIONS.join(', ')}</p>
          <p>Maximum file size: {MAX_FILE_SIZE}</p>
          <p>Maximum rows: {MAX_IMPORT_ROWS.toLocaleString()}</p>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={cancelFileImport}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Continue"
        confirmVariant="primary"
      />
    </div>
  );
};

export default Import; 